import type { TypesenseSearchParams } from '~/utils/typesenseClient'
import type { EventDescription } from '~/composables/types/event'
import { TYPESENSE_GEO_FIELD } from '~/utils/typesenseGeo'
import { stripHtmlToPlainText } from '~/utils/parseHtmlList'
import { eventGetters } from '~/utils/eventGetters'

export interface TypesenseSimilarEventsSourceDoc {
  id: string
  city_slug: string
  /** Typesense `geopoint[]` — one `[lat, lng]` per event location. */
  _geoloc?: [number, number][]
}

/**
 * The page's own categories (KFT order, main first, then the rest incl. parent
 * categories). Every similar showcase stays within them and ranks the main
 * category first (KFT-4193).
 */
export interface SimilarCategories {
  main?: string
  all: string[]
}

/** What the similar showcases need to know about the page they sit on. */
export interface SimilarShowcaseSource {
  permalink: string
  /** KFT hashed id — kits use it to leave themselves out on the KFT path. */
  id: string
  categories: SimilarCategories
  /** Kits (`specialization: KIT`) live in `products_*`, not `events_*`. */
  isKit: boolean
  /**
   * Title + subtitle, embedded at query time for searches that cross
   * collections (kit → online events, event → kits). A stored vector can only
   * be looked up by id inside its own collection, and we never ship vectors to
   * the browser.
   */
  text: string
}

/** Only the fields the similar-events queries build from — never the 384-float embedding. */
export const SIMILAR_EVENTS_SOURCE_FIELDS = `id,city_slug,${TYPESENSE_GEO_FIELD}`

/** 6 in the carousel + 4 in the row below it. */
export const SIMILAR_EVENTS_LIMIT = 10
/**
 * The pool of nearest-by-meaning events that location and category then
 * reorder. Large enough that same-category events nearby usually make it in.
 */
export const SIMILAR_EVENTS_VECTOR_K = 250
/**
 * Everything within this radius counts as equally near. `exclude_radius`, not
 * `precision`: with `precision` an event at the exact same address (usually the
 * same partner) sorts ahead of everything else within the bucket.
 */
export const SIMILAR_EVENTS_NEAR_RADIUS_KM = 50

export const SIMILAR_ONLINE_EVENTS_LIMIT = 8
export const SIMILAR_KITS_LIMIT = 8
/** Online events and kits have no location — category and meaning only. */
export const SIMILAR_NO_GEO_VECTOR_K = 100

const sourceCoords = (source: TypesenseSimilarEventsSourceDoc) => {
  const [lat, lng] = source._geoloc?.[0] ?? []
  return Number.isFinite(lat) && Number.isFinite(lng)
    ? { lat: lat!, lng: lng! }
    : undefined
}

const joinClauses = (clauses: (string | undefined | false)[], sep: string) =>
  clauses.filter(Boolean).join(sep)

const mainCategoryFirst = ({ main }: SimilarCategories) =>
  main ? `_eval(categories.slug:=${main}):desc` : undefined

const inCategories = ({ all }: SimilarCategories) =>
  all.length ? `categories.slug:=[${all.join(',')}]` : undefined

const categoryThenMeaningSort = (categories: SimilarCategories) =>
  joinClauses([mainCategoryFirst(categories), '_vector_distance:asc'], ',')

/** Query-time embedding of the page's text instead of a stored vector. */
const byText = (text: string) => ({
  q: text,
  query_by: 'embedding',
  vector_query: `embedding:([], k: ${SIMILAR_NO_GEO_VECTOR_K})`,
})

/**
 * Similar events: the events that read most like this one, everything within
 * 50 km counting as near, the page's main category first, then by meaning.
 * Online events are excluded: they have no city and a placeholder `_geoloc`.
 */
export const buildVectorSimilarEventsQuery = (
  source: TypesenseSimilarEventsSourceDoc,
  categories: SimilarCategories,
  collection: string
): TypesenseSearchParams => {
  const coords = sourceCoords(source)
  return {
    collection,
    q: '*',
    query_by: 'title',
    filter_by: `id:!=${source.id} && has_events:true && is_online:false`,
    vector_query: `embedding:([], id: ${source.id}, k: ${SIMILAR_EVENTS_VECTOR_K})`,
    per_page: SIMILAR_EVENTS_LIMIT,
    sort_by: joinClauses(
      [
        coords &&
          `${TYPESENSE_GEO_FIELD}(${coords.lat},${coords.lng}, exclude_radius: ${SIMILAR_EVENTS_NEAR_RADIUS_KM}km):asc`,
        mainCategoryFirst(categories),
        '_vector_distance:asc',
      ],
      ','
    ),
  }
}

const onlineEventsFilter = (
  categories: SimilarCategories,
  excludeId?: string
) =>
  joinClauses(
    [
      excludeId && `id:!=${excludeId}`,
      'has_events:true',
      'is_online:true',
      inCategories(categories),
    ],
    ' && '
  )

/** Similar online events from an event's own stored vector, within the page's categories. */
export const buildVectorSimilarOnlineEventsQuery = (
  source: TypesenseSimilarEventsSourceDoc,
  categories: SimilarCategories,
  collection: string
): TypesenseSearchParams => ({
  collection,
  q: '*',
  query_by: 'title',
  filter_by: onlineEventsFilter(categories, source.id),
  vector_query: `embedding:([], id: ${source.id}, k: ${SIMILAR_NO_GEO_VECTOR_K})`,
  per_page: SIMILAR_ONLINE_EVENTS_LIMIT,
  sort_by: categoryThenMeaningSort(categories),
})

/** Similar online events from a kit page's text — kits aren't in `events_*`. */
export const buildTextSimilarOnlineEventsQuery = (
  text: string,
  categories: SimilarCategories,
  collection: string
): TypesenseSearchParams => ({
  collection,
  ...byText(text),
  filter_by: onlineEventsFilter(categories),
  per_page: SIMILAR_ONLINE_EVENTS_LIMIT,
  sort_by: categoryThenMeaningSort(categories),
})

/** Similar kits from a kit's own stored vector (same collection), leaving the kit out. */
export const buildVectorSimilarKitsQuery = (
  kitId: string,
  categories: SimilarCategories,
  collection: string
): TypesenseSearchParams => ({
  collection,
  q: '*',
  query_by: 'title',
  filter_by: joinClauses([inCategories(categories), `id:!=${kitId}`], ' && '),
  vector_query: `embedding:([], id: ${kitId}, k: ${SIMILAR_NO_GEO_VECTOR_K})`,
  per_page: SIMILAR_KITS_LIMIT,
  sort_by: categoryThenMeaningSort(categories),
})

/** Similar kits for an event page, from the event's text — events aren't in `products_*`. */
export const buildTextSimilarKitsQuery = (
  text: string,
  categories: SimilarCategories,
  collection: string
): TypesenseSearchParams => {
  const filterBy = inCategories(categories)
  return {
    collection,
    ...byText(text),
    ...(filterBy && { filter_by: filterBy }),
    per_page: SIMILAR_KITS_LIMIT,
    sort_by: categoryThenMeaningSort(categories),
  }
}

/** The similar showcases' view of the page's event. */
export const getSimilarShowcaseSource = (
  event?: EventDescription
): SimilarShowcaseSource => {
  const slugs = (event?.categories?.data ?? [])
    .map((category) => category?.slug)
    .filter((slug): slug is string => Boolean(slug))
  return {
    permalink: event?.permalink ?? '',
    id: event?.id ?? '',
    categories: {
      main: eventGetters.getMainCategorySlug(event),
      all: [...new Set(slugs)],
    },
    isKit: eventGetters.isBoxEvent(event),
    text: joinClauses(
      [
        stripHtmlToPlainText(event?.title ?? ''),
        stripHtmlToPlainText(event?.subtitle ?? ''),
      ],
      '. '
    ),
  }
}
